// Angular
import { Injectable } from '@angular/core';
// RxJS
import { of, Observable, defer, forkJoin } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
// NGRX
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
// Services
import { AuthService } from '../_services';
// State
import { AppState } from '../../../core/reducers';
// Selectors
// Actions
import {
  AllRoleTemplatesLoaded,
  AllRoleTemplatesRequested,
  RoleTemplateActionTypes,
  RoleTemplatesPageRequested,
  RoleTemplatesPageLoaded,
  RoleTemplateUpdated,
  RoleTemplatesPageToggleLoading,
  RoleTemplateDeleted,
  RoleTemplatesDeleted,
  RoleTemplateOnServerCreated,
  RoleTemplateCreated,
  RoleTemplatesActionToggleLoading,
  RoleTemplatesPageCancelled,
  ThrowRoleTemplateError,
  FireRoleTemplateSuccess,
  RoleTemplateOnServerDeleted,
  RoleTemplatesOnServerDeleted,
  RoleTemplateOnServerUpdated,
  RoleTemplateOnServerActivated,
  RoleTemplateActivated,
} from '../_actions/role-template.actions';

@Injectable()
export class RoleTemplateEffects {
  showPageLoadingDistpatcher = new RoleTemplatesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new RoleTemplatesPageToggleLoading({ isLoading: false });

  showActionLoadingDistpatcher = new RoleTemplatesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new RoleTemplatesActionToggleLoading({ isLoading: false });

  @Effect()
  loadRoleTemplatesPage$ = this.actions$.pipe(
    ofType<RoleTemplatesPageRequested>(RoleTemplateActionTypes.RoleTemplatesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.auth.findRoleTemplates(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.length === 2) {
            const result = res[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery = res[1];
            return new RoleTemplatesPageLoaded({
              roleTemplates: result.pageOfItems,
              totalCount: result.pager.totalCount,
              page: lastQuery,
            });
          } else {
            this.store.dispatch(new ThrowRoleTemplateError({ error: res }));
            return new RoleTemplatesPageCancelled();
          }
        }),
      );
    }),
  );

  @Effect()
  deleteRoleTemplate$ = this.actions$.pipe(
    ofType<RoleTemplateOnServerDeleted>(RoleTemplateActionTypes.RoleTemplateOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.deleteRoleTemplate(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowRoleTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireRoleTemplateSuccess({
                action_type: RoleTemplateActionTypes.RoleTemplateDeleted,
              }),
            );
            return new RoleTemplateDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deleteRoleTemplates$ = this.actions$.pipe(
    ofType<RoleTemplatesOnServerDeleted>(RoleTemplateActionTypes.RoleTemplatesOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.deleteRoleTemplates(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowRoleTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireRoleTemplateSuccess({
                action_type: RoleTemplateActionTypes.RoleTemplatesDeleted,
              }),
            );
            return new RoleTemplatesDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  activateRoleTemplate$ = this.actions$.pipe(
    ofType<RoleTemplateOnServerActivated>(RoleTemplateActionTypes.RoleTemplateOnServerActivated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.activateRoleTemplate(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowRoleTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireRoleTemplateSuccess({
                action_type: RoleTemplateActionTypes.RoleTemplateActivated,
              }),
            );
            return new RoleTemplateActivated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateRoleTemplate$ = this.actions$.pipe(
    ofType<RoleTemplateOnServerUpdated>(RoleTemplateActionTypes.RoleTemplateOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.updateRoleTemplate(payload.roleTemplate).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowRoleTemplateError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireRoleTemplateSuccess({
                action_type: RoleTemplateActionTypes.RoleTemplateUpdated,
              }),
            );
            return new RoleTemplateUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createRoleTemplate$ = this.actions$.pipe(
    ofType<RoleTemplateOnServerCreated>(RoleTemplateActionTypes.RoleTemplateOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.createRoleTemplate(payload.roleTemplate).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (!res.id) {
            return new ThrowRoleTemplateError({ error: res });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireRoleTemplateSuccess({
                action_type: RoleTemplateActionTypes.RoleTemplateCreated,
              }),
            );
            return new RoleTemplateCreated({ roleTemplate: res });
          }
        }),
      );
    }),
  );

  @Effect()
  init$: Observable<Action> = defer(() => {
    const allRoleTemplate = localStorage.getItem('allRoleTemplate');
    if (allRoleTemplate) {
      return of(
        new AllRoleTemplatesLoaded({
          roles: JSON.parse(allRoleTemplate),
        }),
      );
    }
    return of(new AllRoleTemplatesRequested());
  });

  constructor(
    private actions$: Actions,
    private auth: AuthService,
    private store: Store<AppState>,
  ) {}
}
